import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Quiz as QuizIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Button from '../UIKit/Button';
import { getTestStatusAndResult } from '../../utils/testUtils';

/**
 * Компонент карточки теста
 * @param {Object} test - объект теста
 * @param {Object} userProgress - прогресс пользователя
 * @param {Function} onStartTest - обработчик начала теста
 * @param {Function} onViewTest - обработчик просмотра теста
 * @param {boolean} hasSpecialAccess - есть ли специальный доступ
 * @param {string} variant - вариант отображения ('card' | 'list')
 */
const TestCard = ({
  test,
  userProgress,
  onStartTest,
  onViewTest,
  hasSpecialAccess = false,
  variant = 'card',
}) => {
  const { t } = useTranslation(['courses', 'common']);
  const { status, result } = getTestStatusAndResult(test, userProgress);

  const handleStartTest = () => {
    if (onStartTest) {
      onStartTest(test._id);
    }
  };

  const handleViewTest = () => {
    if (onViewTest) {
      onViewTest(test._id);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'passed':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />;
      case 'failed':
        return <CancelIcon sx={{ color: 'error.main', fontSize: 28 }} />;
      case 'locked':
        return <LockIcon sx={{ color: 'text.disabled', fontSize: 28 }} />;
      default:
        return <QuizIcon sx={{ color: 'text.secondary', fontSize: 28 }} />;
    }
  };

  const getStatusChip = () => {
    switch (status) {
      case 'passed':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label={t('common:status.completed')}
            color="success"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        );
      case 'failed':
        return (
          <Chip
            icon={<CancelIcon />}
            label={t('common:status.testFailed')}
            color="error"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        );
      case 'locked':
        return (
          <Chip
            icon={<LockIcon />}
            label={t('courses:courseDetail.lessonStatus.locked')}
            disabled
            size="small"
            sx={{ fontWeight: 600 }}
          />
        );
      default:
        return null;
    }
  };

  const getCardStyles = () => {
    const baseStyles = {
      p: 2,
      borderRadius: 2,
      border: '1px solid',
    };

    switch (status) {
      case 'passed':
        return {
          ...baseStyles,
          borderColor: 'success.main',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
        };
      case 'failed':
        return {
          ...baseStyles,
          borderColor: 'error.main',
          backgroundColor: 'rgba(244, 67, 54, 0.1)',
        };
      case 'locked':
        return {
          ...baseStyles,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          opacity: 0.6,
        };
      default:
        return {
          ...baseStyles,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        };
    }
  };

  if (variant === 'list') {
    return (
      <Box sx={getCardStyles()}>
        {/* Test Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
              {test.title || `${t('courses:courseDetail.stats.test')} ${test.index + 1}`}
            </Typography>
            {test.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {test.description}
              </Typography>
            )}
          </Box>
          {getStatusIcon()}
        </Box>

        {/* Test Stats */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap', textTransform: 'capitalize' }}>
          {test.questions?.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('courses:courseDetail.stats.questions')}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {test.questions.length}
              </Typography>
            </Box>
          )}
          {test.timeLimit && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('courses:courseDetail.stats.timeLimit')}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {test.timeLimit} {t('common:time.shortMin')}
              </Typography>
            </Box>
          )}
          {test.passingScore && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('courses:courseDetail.stats.passingScore')}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {test.passingScore}%
              </Typography>
            </Box>
          )}
          {result && result.score !== undefined && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('courses:courseDetail.stats.yourScore')}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{
                  color: status === 'passed' ? 'success.main' : 'error.main'
                }}
              >
                {Math.round(result.score)}%
              </Typography>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {status === 'available' && (
            <>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleStartTest}
                sx={{ py: 1, flex: 1 }}
              >
                {t('courses:courseDetail.lessonCompletion.startTest')}
              </Button>
              {hasSpecialAccess && (
                <Button
                  variant="outlined"
                  onClick={handleViewTest}
                  sx={{ py: 1 }}
                >
                  {t('common:actions.view')}
                </Button>
              )}
            </>
          )}

          {(status === 'passed' || status === 'failed') && (
            <>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleStartTest}
                sx={{ py: 1, flex: 1 }}
              >
                {t('common:actions.retry')}
              </Button>
              {hasSpecialAccess && (
                <Button
                  variant="outlined"
                  onClick={handleViewTest}
                  sx={{ py: 1 }}
                >
                  {t('common:actions.view')}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  }

  // Card variant (для grid view)
  return (
    <Box sx={getCardStyles()}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {test.title || `${t('courses:courseDetail.stats.test')} ${test.index + 1}`}
        </Typography>
        {getStatusIcon()}
      </Box>

      {test.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {test.description}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {test.questions?.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('courses:courseDetail.stats.questions')}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {test.questions.length}
            </Typography>
          </Box>
        )}
        {test.timeLimit && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('courses:courseDetail.stats.timeLimit')}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {test.timeLimit} {t('common:time.shortMin')}
            </Typography>
          </Box>
        )}
      </Box>

      {result && result.score !== undefined && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('courses:courseDetail.stats.yourScore')}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              color: status === 'passed' ? 'success.main' : 'error.main'
            }}
          >
            {Math.round(result.score)}%
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {status === 'available' && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleStartTest}
            fullWidth
            sx={{ py: 1 }}
          >
            {t('courses:courseDetail.lessonCompletion.startTest')}
          </Button>
        )}

        {(status === 'passed' || status === 'failed') && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleStartTest}
            fullWidth
            sx={{ py: 1 }}
          >
            {t('common:actions.retry')}
          </Button>
        )}
      </Box>

      {getStatusChip() && (
        <Box sx={{ mt: 2 }}>
          {getStatusChip()}
        </Box>
      )}
    </Box>
  );
};

export default TestCard;
